import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { fetchAccountProfiles, type ProfileSummary } from "../profile/profile.api";
import {
  ACCOUNT_PROFILES_STORAGE_KEY,
  ACTIVE_PROFILE_UUID_STORAGE_KEY,
  clearActiveProfileUuid as clearPersistedActiveProfileUuid,
  clearStoredProfiles,
  LEGACY_ACCOUNT_PROVIDERS_STORAGE_KEY,
  LEGACY_ACTIVE_PROVIDER_UUID_STORAGE_KEY,
  readActiveProfileUuid,
  readStoredProfiles,
  setActiveProfileUuid as persistActiveProfileUuid,
  setStoredProfiles,
} from "../profile/active-profile.storage";

import {
  signInRequest,
  signInWithAppleRequest,
  signInWithGoogleRequest,
  signUpRequest,
  type AuthAccount,
  type AuthPayload,
  type SignInInput,
  type SignInWithAppleInput,
  type SignInWithGoogleInput,
  type SignUpInput,
} from "./auth.api";
import { trackAdminAnalyticsEvent } from "lib/marketing-analytics";
import {
  clearAccessTokenCookie,
  clearLegacyAuthSessionStorage,
  migrateLegacyAuthSessionToCookie,
  parseAccountFromAccessToken,
  persistAccessTokenToCookie,
  readAccessTokenFromCookie,
} from "./auth-cookie";
import { showSuiErrorSnackbar } from "components/SuiSnackbar";

type SessionPersistence = "local" | "session";

interface AuthSession {
  accessToken: string;
  account: AuthAccount;
}

interface AuthContextValue {
  account: AuthAccount | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  profiles: ProfileSummary[];
  activeProfileUuid: string | null;
  activeProfile: ProfileSummary | null;
  isProfilesLoading: boolean;
  profilesErrorMessage: string;
  refreshProfiles: () => Promise<void>;
  setActiveProfileUuid: (profileUuid: string | null) => void;
  registerProfile: (profile: ProfileSummary) => void;
  signIn: (
    input: SignInInput,
    options?: { remember?: boolean }
  ) => Promise<{ activeProfileUuid: string | null; isNewAccount: boolean }>;
  signInWithGoogle: (
    input: SignInWithGoogleInput,
    options?: { remember?: boolean }
  ) => Promise<{ activeProfileUuid: string | null; isNewAccount: boolean }>;
  signInWithApple: (
    input: SignInWithAppleInput,
    options?: { remember?: boolean }
  ) => Promise<{ activeProfileUuid: string | null; isNewAccount: boolean }>;
  signUp: (
    input: SignUpInput
  ) => Promise<{ activeProfileUuid: string | null; isNewAccount: boolean }>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredSession(): AuthSession | null {
  const accessToken = readAccessTokenFromCookie();
  if (accessToken) {
    const account = parseAccountFromAccessToken(accessToken);
    if (!account) {
      clearAccessTokenCookie();
      return null;
    }

    return {
      accessToken,
      account,
    };
  }

  return migrateLegacyAuthSessionToCookie();
}

function persistSession(session: AuthSession, persistence: SessionPersistence): void {
  persistAccessTokenToCookie(session.accessToken, {
    persistent: persistence === "local",
  });
  clearLegacyAuthSessionStorage();
}

function clearStoredSession(): void {
  clearAccessTokenCookie();
  clearLegacyAuthSessionStorage();
}

function resolveActiveProfileUuid(
  profiles: ProfileSummary[],
  candidateProfileUuid: string | null
): string | null {
  if (!profiles.length) {
    return null;
  }

  if (candidateProfileUuid && profiles.some((profile) => profile.uuid === candidateProfileUuid)) {
    return candidateProfileUuid;
  }

  return profiles[0].uuid;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [profiles, setProfiles] = useState<ProfileSummary[]>(() => readStoredProfiles());
  const [activeProfileUuid, setActiveProfileUuidState] = useState<string | null>(() =>
    readActiveProfileUuid()
  );
  const [isProfilesLoading, setIsProfilesLoading] = useState(false);
  const [profilesErrorMessage, setProfilesErrorMessage] = useState("");
  const profilesRequestIdRef = useRef(0);

  useEffect(() => {
    if (profilesErrorMessage) {
      showSuiErrorSnackbar(profilesErrorMessage);
    }
  }, [profilesErrorMessage]);

  useEffect(() => {
    setSession(readStoredSession());
    setIsInitializing(false);
  }, []);

  const clearProfileState = useCallback(() => {
    clearStoredProfiles();
    clearPersistedActiveProfileUuid();
    setProfiles([]);
    setActiveProfileUuidState(null);
    setProfilesErrorMessage("");
    setIsProfilesLoading(false);
  }, []);

  const applyProfilesSnapshot = useCallback((nextProfiles: ProfileSummary[]) => {
    setProfiles(nextProfiles);
    setStoredProfiles(nextProfiles);

    const resolvedActiveProfileUuid = resolveActiveProfileUuid(
      nextProfiles,
      readActiveProfileUuid()
    );

    if (!resolvedActiveProfileUuid) {
      clearPersistedActiveProfileUuid();
      setActiveProfileUuidState(null);
      return;
    }

    persistActiveProfileUuid(resolvedActiveProfileUuid);
    setActiveProfileUuidState(resolvedActiveProfileUuid);
  }, []);

  const setAuthenticatedSession = useCallback((authPayload: AuthPayload, remember: boolean) => {
    const nextSession: AuthSession = {
      accessToken: authPayload.accessToken,
      account: authPayload.account,
    };

    persistSession(nextSession, remember ? "local" : "session");
    setSession(nextSession);
  }, []);

  const resolveNewAccountProfile = useCallback(
    async (
      payload: AuthPayload,
      remember: boolean,
      registrationMethod: "credentials" | "google" | "apple"
    ): Promise<{ activeProfileUuid: string | null; isNewAccount: boolean }> => {
      const requestId = profilesRequestIdRef.current + 1;
      profilesRequestIdRef.current = requestId;

      setIsProfilesLoading(true);
      setProfilesErrorMessage("");

      try {
        const accountProfiles = await fetchAccountProfiles(
          payload.account.uuid,
          payload.accessToken
        );

        if (profilesRequestIdRef.current !== requestId) {
          return {
            activeProfileUuid: null,
            isNewAccount: true,
          };
        }

        if (!accountProfiles.length) {
          throw new Error("Unable to resolve created profile.");
        }

        setAuthenticatedSession(payload, remember);
        applyProfilesSnapshot(accountProfiles);
        trackAdminAnalyticsEvent("admin_registration_completed", {
          registration_method: registrationMethod,
        });

        return {
          activeProfileUuid:
            resolveActiveProfileUuid(accountProfiles, readActiveProfileUuid()) ??
            accountProfiles[0]?.uuid ??
            null,
          isNewAccount: true,
        };
      } catch (error) {
        if (profilesRequestIdRef.current !== requestId) {
          return {
            activeProfileUuid: null,
            isNewAccount: true,
          };
        }

        throw error instanceof Error ? error : new Error("Unable to load profiles.");
      } finally {
        if (profilesRequestIdRef.current === requestId) {
          setIsProfilesLoading(false);
        }
      }
    },
    [applyProfilesSnapshot, setAuthenticatedSession]
  );

  const refreshProfiles = useCallback(async () => {
    if (!session?.account.uuid) {
      clearProfileState();
      return;
    }

    const requestId = profilesRequestIdRef.current + 1;
    profilesRequestIdRef.current = requestId;

    setIsProfilesLoading(true);
    setProfilesErrorMessage("");

    try {
      const accountProfiles = await fetchAccountProfiles(session.account.uuid, session.accessToken);

      if (profilesRequestIdRef.current !== requestId) {
        return;
      }

      applyProfilesSnapshot(accountProfiles);
    } catch (error) {
      if (profilesRequestIdRef.current !== requestId) {
        return;
      }

      setProfilesErrorMessage(error instanceof Error ? error.message : "Unable to load profiles.");
    } finally {
      if (profilesRequestIdRef.current === requestId) {
        setIsProfilesLoading(false);
      }
    }
  }, [applyProfilesSnapshot, clearProfileState, session?.account.uuid, session?.accessToken]);

  const setActiveProfileUuid = useCallback((profileUuid: string | null) => {
    const normalizedProfileUuid = profileUuid?.trim();
    if (!normalizedProfileUuid) {
      clearPersistedActiveProfileUuid();
      setActiveProfileUuidState(null);
      return;
    }

    persistActiveProfileUuid(normalizedProfileUuid);
    setActiveProfileUuidState(normalizedProfileUuid);
  }, []);

  const registerProfile = useCallback((profile: ProfileSummary) => {
    const profileUuid = profile.uuid?.trim();
    if (!profileUuid) {
      return;
    }

    const normalizedProfile: ProfileSummary = {
      ...profile,
      uuid: profileUuid,
      title: profile.title?.trim() || "Profile",
    };

    setProfiles((currentProfiles) => {
      const nextProfiles = [
        normalizedProfile,
        ...currentProfiles.filter((item) => item.uuid !== normalizedProfile.uuid),
      ];
      setStoredProfiles(nextProfiles);
      return nextProfiles;
    });

    persistActiveProfileUuid(normalizedProfile.uuid);
    setActiveProfileUuidState(normalizedProfile.uuid);
    setProfilesErrorMessage("");
  }, []);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    void refreshProfiles();
  }, [isInitializing, refreshProfiles]);

  useEffect(() => {
    function handleStorageChange(event: StorageEvent) {
      if (
        event.key === ACTIVE_PROFILE_UUID_STORAGE_KEY ||
        event.key === LEGACY_ACTIVE_PROVIDER_UUID_STORAGE_KEY
      ) {
        setActiveProfileUuidState(readActiveProfileUuid());
        return;
      }

      if (
        event.key === ACCOUNT_PROFILES_STORAGE_KEY ||
        event.key === LEGACY_ACCOUNT_PROVIDERS_STORAGE_KEY
      ) {
        setProfiles(readStoredProfiles());
      }
    }

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!profiles.length) {
      if (activeProfileUuid) {
        clearPersistedActiveProfileUuid();
        setActiveProfileUuidState(null);
      }
      return;
    }

    if (activeProfileUuid && profiles.some((profile) => profile.uuid === activeProfileUuid)) {
      return;
    }

    const fallbackProfileUuid = profiles[0].uuid;
    persistActiveProfileUuid(fallbackProfileUuid);
    setActiveProfileUuidState(fallbackProfileUuid);
  }, [activeProfileUuid, profiles]);

  const signIn = useCallback(
    async (input: SignInInput, options?: { remember?: boolean }) => {
      const payload = await signInRequest(input);
      setAuthenticatedSession(payload, Boolean(options?.remember));
      return {
        activeProfileUuid: null,
        isNewAccount: false,
      };
    },
    [setAuthenticatedSession]
  );

  const signInWithGoogle = useCallback(
    async (input: SignInWithGoogleInput, options?: { remember?: boolean }) => {
      const payload = await signInWithGoogleRequest(input);

      if (payload.isNewAccount) {
        return resolveNewAccountProfile(payload, Boolean(options?.remember), "google");
      }

      setAuthenticatedSession(payload, Boolean(options?.remember));
      return {
        activeProfileUuid: null,
        isNewAccount: false,
      };
    },
    [resolveNewAccountProfile, setAuthenticatedSession]
  );

  const signInWithApple = useCallback(
    async (input: SignInWithAppleInput, options?: { remember?: boolean }) => {
      const payload = await signInWithAppleRequest(input);

      if (payload.isNewAccount) {
        return resolveNewAccountProfile(payload, Boolean(options?.remember), "apple");
      }

      setAuthenticatedSession(payload, Boolean(options?.remember));
      return {
        activeProfileUuid: null,
        isNewAccount: false,
      };
    },
    [resolveNewAccountProfile, setAuthenticatedSession]
  );

  const signUp = useCallback(
    async (input: SignUpInput) => {
      const payload = await signUpRequest(input);
      return resolveNewAccountProfile(payload, false, "credentials");
    },
    [resolveNewAccountProfile]
  );

  const signOut = useCallback(() => {
    profilesRequestIdRef.current += 1;
    clearStoredSession();
    setSession(null);
    clearProfileState();
  }, [clearProfileState]);

  const activeProfile = useMemo<ProfileSummary | null>(() => {
    if (!profiles.length) {
      return null;
    }

    if (!activeProfileUuid) {
      return profiles[0] ?? null;
    }

    return profiles.find((profile) => profile.uuid === activeProfileUuid) ?? profiles[0] ?? null;
  }, [activeProfileUuid, profiles]);

  const authContextValue = useMemo<AuthContextValue>(
    () => ({
      account: session?.account ?? null,
      accessToken: session?.accessToken ?? null,
      isAuthenticated: Boolean(session?.accessToken),
      isInitializing,
      profiles,
      activeProfileUuid,
      activeProfile,
      isProfilesLoading,
      profilesErrorMessage,
      refreshProfiles,
      setActiveProfileUuid,
      registerProfile,
      signIn,
      signInWithApple,
      signInWithGoogle,
      signUp,
      signOut,
    }),
    [
      activeProfile,
      activeProfileUuid,
      isInitializing,
      isProfilesLoading,
      profiles,
      profilesErrorMessage,
      refreshProfiles,
      registerProfile,
      session,
      setActiveProfileUuid,
      signIn,
      signInWithApple,
      signInWithGoogle,
      signOut,
      signUp,
    ]
  );

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }

  return authContext;
}
